<template>
  <div v-if="error" class="feedback-container">
    <div class="subtitle-2">
      {{ error.message }}
    </div>
  </div>
  <div v-else-if="!user" class="page-loading">
    <AttensiLoadingSpinner />
  </div>
  <template v-else>
    <TheHeader :title="$t('facilitator_tools.header')" />
    <div class="container">
      <div class="form-grid">
        <AttensiInput
          v-model="title"
          label="Leaderboard title (optional)"
          name="title"
          placeholder="Onboarding leaderboard 12"
        />
      </div>
      <div class="form-grid">
        <DropdownWrapper
          v-model="product"
          :label="$t('facilitator_tools.product_label')"
          :placeholder="$t('facilitator_tools.product_placeholder')"
          name="product"
          :options="productOptions ?? []"
        />
        <DropdownWrapper
          v-model="group"
          :label="$t('facilitator_tools.group_label')"
          :placeholder="$t('facilitator_tools.group_placeholder')"
          name="group"
          :options="groupOptionsWithCreate ?? []"
        />

        <DropdownWrapper
          v-model="scenario"
          :label="$t('facilitator_tools.scenario_label')"
          :placeholder="$t('facilitator_tools.scenario_placeholder')"
          name="scenario"
          :options="scenarioOptions ?? []"
        />
      </div>
      <div class="form-grid">
        <AttensiInput
          v-model="limit"
          :label="$t('facilitator_tools.limit_positions_label')"
          name="limit-positions"
          type="number"
          placeholder=""
        />
      </div>
      <div class="form-grid">
        <AttensiInput
          v-model="redirectUrl"
          :label="$t('facilitator_tools.redirect_url_label')"
          :info-text="$t('facilitator_tools.redirect_url_info')"
          name="redirect-url"
          placeholder="https://portal.attensi.com/product/123456"
        />
      </div>
      <div class="form-grid">
        <AttensiButton
          :disabled="isGeneratingLeaderboard"
          class="button"
          size="large"
          shape="round"
          @click="submit"
        >
          {{ $t('facilitator_tools.submit_button') }}
        </AttensiButton>
      </div>
      <div v-if="isGeneratingLeaderboard" class="loading">
        <AttensiLoadingSpinner />
      </div>
      <div v-else-if="urls" class="urls">
        <div class="form-grid">
          <LabelLink
            :url="urls.leaderboardUrl"
            :label="$t('facilitator_tools.leaderboard_url_label')"
            :info-text="$t('facilitator_tools.leaderboard_url_info')"
          />
        </div>
        <div v-if="urls.inviteUrl" class="form-grid">
          <LabelLink
            :url="urls.inviteUrl"
            :info-text="$t('facilitator_tools.invite_url_info')"
            :label="$t('facilitator_tools.invite_url_label')"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<script lang="ts">
import DropdownWrapper from '@/components/DropdownWrapper.vue'
import TheHeader from '@/components/TheHeader.vue'
import {
  CREATE_NEW_LEADERBOARD_GROUP,
  SELECT_EVERYONE,
  generateLeaderboardUrls,
  LeaderboardUrls,
} from '@/helpers/generateLeaderboardUrls'
import {
  useGroupOptions,
  useProductOptions,
  useScenarioOptions,
} from '@/hooks/useDropdownOptions'
import { useMyUser } from '@/hooks/useMyUser'
import { anyError } from '@/utils/anyError'
import { DropdownOption } from '@/types/DropdownOption'
import {
  AttensiButton,
  AttensiInput,
  AttensiLoadingSpinner,
} from '@attensi/materia'
import { computed, defineComponent, ref } from 'vue'
import LabelLink from '@/components/LabelLink.vue'
import { DEFAULT_LEADERBOARD_LIMIT } from '@/constants'
import { GroupType } from '@/api/types'
import { useCurrentCompany } from '@/hooks/useCurrentCompany'
import { useI18n } from 'vue-i18n'
import { initializeAppLauncher } from '../helpers/appLauncher'

export default defineComponent({
  components: {
    AttensiButton,
    AttensiInput,
    TheHeader,
    AttensiLoadingSpinner,
    DropdownWrapper,
    LabelLink,
  },
  setup() {
    initializeAppLauncher()
    window.document.title = 'Create leaderboard'

    const { user, error: e1 } = useMyUser()
    const { company, error: e5 } = useCurrentCompany()
    const { t } = useI18n()

    const title = ref('')
    const product = ref<DropdownOption>()
    const group = ref<DropdownOption>()
    const scenario = ref<DropdownOption>()
    const limit = ref(DEFAULT_LEADERBOARD_LIMIT)
    const redirectUrl = ref('')

    const userId = computed(() => user.value?.id)
    const productId = computed(() => product.value?.product.id)

    const { productOptions, error: e2 } = useProductOptions(userId)
    const { groupOptions, error: e3 } = useGroupOptions(userId)
    const { scenarioOptions, error: e4 } = useScenarioOptions(userId, productId)

    const groupOptionsWithCreate = computed(() => {
      if (groupOptions.value === undefined) return

      const newGroupOption: DropdownOption = {
        id: -1,
        title: t('facilitator_tools.new_group'),
        description: t('facilitator_tools.new_group_helper_text'),
        group: {
          id: CREATE_NEW_LEADERBOARD_GROUP,
          group_type: GroupType.LEADERBOARD,
        },
      }
      const everyOneGroupOption: DropdownOption = {
        id: -2,
        title: t('facilitator_tools.everyone'),
        description: t('facilitator_tools.everyone_helper_text'),
        group: {
          id: SELECT_EVERYONE,
          group_type: undefined,
        },
      }
      return [everyOneGroupOption, newGroupOption, ...groupOptions.value]
    })

    return {
      user,
      company,
      title,
      product,
      group,
      scenario,
      limit,
      redirectUrl,
      productOptions,
      groupOptionsWithCreate,
      scenarioOptions,
      error: anyError(e1, e2, e3, e4, e5),
    }
  },
  data() {
    return {
      isGeneratingLeaderboard: false,
      urls: undefined as LeaderboardUrls | undefined,
    }
  },
  methods: {
    async submit() {
      this.isGeneratingLeaderboard = true

      try {
        this.urls = await generateLeaderboardUrls({
          user: this.user!,
          company: this.company!,
          productId: this.product?.product.id,
          groupId: this.group?.group.id,
          groupType: this.group?.group.group_type,
          scenarioId: this.scenario?.scenario.scenario_id,
          redirectUrl: this.redirectUrl,
          title: this.title,
          limit: this.limit,
        })
      } catch (e) {
        this.error = e
      } finally {
        this.isGeneratingLeaderboard = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 100px 0;
}

.title {
  font-size: 20px;
  margin: 0 auto;
  text-align: center;
  margin: 25px 0;
}

.form-grid {
  margin: 20px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

.button {
  color: $canvas; // Materia css is messed up. This can probably be removed when updating the Materia vue3 branch

  @media (min-width: 800px) {
    justify-self: center;
    padding-left: 100px !important;
    padding-right: 100px !important;
  }
}

.urls {
  margin: 50px 0;
}
</style>
